import React, { useState } from 'react';
import { useNavigate, Navigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import Logo from '@/components/Logo';
import { Loader2, AlertCircle, CheckCircle2 } from 'lucide-react';

export default function LoginPage() {
    const { user, login } = useAuth();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [keepSignedIn, setKeepSignedIn] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const [flashBanner, setFlashBanner] = useState(() => {
        if (searchParams.get('welcome')) return 'Welcome! Your password is set. Sign in below to get started.';
        if (searchParams.get('reset'))   return 'Password updated. Sign in with your new password.';
        return '';
    });

    if (user) {
        const redir = searchParams.get('redirect');
        return <Navigate to={redir && redir.startsWith('/') ? redir : '/'} replace />;
    }

    const submit = async (e) => {
        e.preventDefault();
        setSubmitting(true); setError(''); setFlashBanner('');
        try {
            await login(email.trim().toLowerCase(), password);
            const redir = searchParams.get('redirect');
            navigate(redir && redir.startsWith('/') ? redir : '/');
        } catch (err) {
            setError(err?.response?.data?.detail || 'Login failed. Try again.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-page flex flex-col">
            <div className="mx-auto max-w-[1440px] w-full px-3 md:px-6 py-3 md:py-6 flex-1 flex">
                <div className="shine-shell flex-1 overflow-hidden">
                    <div className="h-full grid lg:grid-cols-2 gap-10 lg:gap-16 p-6 md:p-10 lg:p-16 items-center">
                        {/* Left — marketing side */}
                        <div className="space-y-8">
                            <Logo variant="plain" size="lg" />
                            <div>
                                <h1 className="font-light leading-[1.02] text-ink text-5xl md:text-6xl lg:text-[64px]"
                                    style={{ letterSpacing: '-0.025em' }}>
                                    Run your agency,<br />
                                    the <span style={{ color: 'rgb(var(--green))' }}>SHINE</span> way.
                                </h1>
                                <p className="mt-5 text-ink-2 max-w-md text-[15px] leading-relaxed">
                                    Central hub for SOPs, tasks, clients, content calendars, leads and rate cards —
                                    built for the SHINE AD AGENCY team in Vizag.
                                </p>
                            </div>
                            <ul className="space-y-2.5">
                                {[
                                    'Searchable SOP library with version control',
                                    'Role-based access for the entire team',
                                    'Daily reports, attendance and performance',
                                    'Mobile-first — works great on phones',
                                ].map((t) => (
                                    <li key={t} className="flex items-center gap-3 text-ink-2 text-[14px]">
                                        <span className="h-2 w-2 rounded-full flex-shrink-0" style={{ background: 'rgb(var(--green))' }} />
                                        {t}
                                    </li>
                                ))}
                            </ul>
                            <div className="hidden md:flex gap-10 pt-2">
                                <div>
                                    <div className="text-[42px] font-light leading-none" style={{ letterSpacing: '-0.02em' }}>500+</div>
                                    <div className="text-ink-3 text-[13px] mt-1">Brands served</div>
                                </div>
                                <div>
                                    <div className="text-[42px] font-light leading-none" style={{ letterSpacing: '-0.02em' }}>2016</div>
                                    <div className="text-ink-3 text-[13px] mt-1">Since</div>
                                </div>
                            </div>
                        </div>

                        {/* Right — login card */}
                        <div className="bg-white rounded-card p-6 md:p-10"
                            style={{ boxShadow: '0 24px 60px rgba(17,20,24,.10)' }}>
                            <h2 className="text-[28px] font-light text-ink leading-tight"
                                style={{ letterSpacing: '-0.01em' }}>Team sign in</h2>
                            <p className="text-ink-2 mt-1 text-[14px]">Welcome back. Sign in to access the Ops Hub.</p>

                            <form className="mt-6 space-y-4" onSubmit={submit}>
                                {flashBanner && (
                                    <div className="flex items-start gap-2 text-sm rounded-input px-3 py-2.5 border"
                                        style={{ background: 'rgb(var(--green-tint))', borderColor: 'rgba(0,168,89,.3)', color: 'rgb(var(--green-ink))' }}
                                        data-testid="login-flash">
                                        <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" />
                                        <span>{flashBanner}</span>
                                    </div>
                                )}
                                <div>
                                    <Label htmlFor="email" className="text-ink-2 text-[13px]">Email</Label>
                                    <Input
                                        id="email"
                                        data-testid="login-email"
                                        type="email"
                                        required
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        placeholder="you@shineadagency.com"
                                        className="mt-1.5"
                                    />
                                </div>
                                <div>
                                    <div className="flex items-baseline justify-between">
                                        <Label htmlFor="password" className="text-ink-2 text-[13px]">Password</Label>
                                        <Link
                                            to="/forgot-password"
                                            className="text-xs font-medium"
                                            style={{ color: 'rgb(var(--green-ink))' }}
                                            data-testid="login-forgot-link"
                                        >
                                            Forgot password?
                                        </Link>
                                    </div>
                                    <Input
                                        id="password"
                                        data-testid="login-password"
                                        type="password"
                                        required
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        placeholder="••••••••"
                                        className="mt-1.5"
                                    />
                                </div>
                                <label className="flex items-center gap-2.5 text-[13px] text-ink-2 cursor-pointer pt-1">
                                    <input
                                        type="checkbox"
                                        checked={keepSignedIn}
                                        onChange={(e) => setKeepSignedIn(e.target.checked)}
                                        className="h-4 w-4 rounded accent-green"
                                        data-testid="login-keep-signed-in"
                                    />
                                    Keep me signed in on this device
                                </label>
                                {error && (
                                    <div className="flex items-center gap-2 text-sm rounded-input px-3 py-2.5 border"
                                        style={{ background: 'rgba(180,35,24,.08)', borderColor: 'rgba(180,35,24,.3)', color: 'rgb(var(--danger))' }}>
                                        <AlertCircle className="h-4 w-4" />
                                        {error}
                                    </div>
                                )}
                                <Button
                                    data-testid="login-submit"
                                    type="submit"
                                    disabled={submitting}
                                    className="w-full h-12 rounded-pill font-medium text-[15px]"
                                    style={{ background: 'rgb(var(--ink))', color: '#ffffff' }}
                                >
                                    {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Sign in'}
                                </Button>
                                <p className="text-center text-ink-3 text-[12px] pt-1">
                                    Access is role-based and set by your admin.
                                </p>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
            <footer className="text-center text-xs text-ink-3 py-3">
                © {new Date().getFullYear()}-{String((new Date().getFullYear() + 1) % 100).padStart(2, "0")} SHINE AD AGENCY · Vizag · Hyderabad
            </footer>
        </div>
    );
}
